import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { SharetribeApi } from '../../util/api';
import { ReviewAction, StatusCode } from '../../util/enums';
import { showToaster } from '../../util/genericHelpers';
import { createSlug } from '../../util/urlHelpers';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SHOW_LISTING_REQUEST = 'app/AdminDreamListingReviewPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/AdminDreamListingReviewPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/AdminDreamListingReviewPage/SHOW_LISTING_ERROR';

export const REVIEW_LISTING_REQUEST = 'app/AdminDreamListingReviewPage/REVIEW_LISTING_REQUEST';
export const REVIEW_LISTING_SUCCESS = 'app/AdminDreamListingReviewPage/REVIEW_LISTING_SUCCESS';
export const REVIEW_LISTING_ERROR = 'app/AdminDreamListingReviewPage/REVIEW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingInProgress: false,
  showListingError: null,
  reviewListingInProgress: false,
  rejectListingInProgress: false,
  reviewListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingInProgress: true, showListingError: null };
    case SHOW_LISTING_SUCCESS:
      return { ...state, showListingInProgress: false, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingInProgress: false, showListingError: payload };
    case REVIEW_LISTING_REQUEST:
      return {
        ...state,
        reviewListingInProgress: payload === ReviewAction.ACCEPT,
        rejectListingInProgress: payload === ReviewAction.REJECT,
        reviewListingError: null,
      };
    case REVIEW_LISTING_SUCCESS:
      return {
        ...state,
        rejectListingInProgress: false,
        reviewListingInProgress: false,
        reviewListingError: null,
      };
    case REVIEW_LISTING_ERROR:
      return {
        ...state,
        rejectListingInProgress: false,
        reviewListingInProgress: false,
        reviewListingError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const reviewListingInProgressSelector = state =>
  state.AdminDreamListingReviewPage.reviewListingInProgress;
export const rejectListingInProgressSelector = state =>
  state.AdminDreamListingReviewPage.rejectListingInProgress;
export const showListingInProgressSelector = state =>
  state.AdminDreamListingReviewPage.showListingInProgress;

// ================ Action creators ================ //

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});
export const showListingSuccess = () => ({
  type: SHOW_LISTING_SUCCESS,
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const reviewListingRequest = action => ({ type: REVIEW_LISTING_REQUEST, payload: action });
export const reviewListingSuccess = () => ({
  type: REVIEW_LISTING_SUCCESS,
});
export const reviewListingError = e => ({
  type: REVIEW_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //
/**
 * Fetches and displays a listing with the specified image configuration.
 *
 * @param {string} listingId - The ID of the listing to display.
 * @param {object} config - The configuration object that includes layout and listing details.
 * @returns {Promise<object>} The fetched listing data.
 */
export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  // Early validation for the required listingId parameter
  if (!listingId) {
    const error = new Error('Listing ID is required.');
    dispatch(showListingError(storableError(error)));
    return Promise.reject(error);
  }
  // Destructure listing image configuration safely from config
  const { layout: { listingImage = {} } = {} } = config;
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Dispatch actions to indicate the beginning of the listing fetch process
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  // Build the parameters for fetching the listing, including various image variants
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.default',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.facebook',
      'variants.twitter',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  try {
    // Fetch the listing data using the SDK
    const response = await sdk.listings.show(params);
    const listingData = response.data;

    // Sanitize listing data by applying any listing-specific field configuration
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    // Dispatch an action to add the retrieved marketplace entities into the store
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(showListingSuccess());
    // Return the retrieved listing data
    return response;
  } catch (error) {
    // Dispatch an error action and reject the promise with the error
    dispatch(showListingError(storableError(error)));
    return Promise.reject(error);
  }
};

export const reviewListing = ({ listing, action, config }) => async (dispatch, getState, sdk) => {
  dispatch(reviewListingRequest(action));
  try {
    const { uuid } = listing.id;
    const title = listing.attributes?.title;
    const baseUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
    const emailParams = {
      estimationToolUrl: `${baseUrl}/supplier/cost-estimation-tool`,
      documentsUrl: `${baseUrl}/document-management?listingId=${uuid}`,
      projectUrl: `${baseUrl}/review-brief/${createSlug(title)}/${uuid}`,
      listingId: uuid,
    };

    const handleSuccess = message => {
      showToaster('success', message);
      dispatch(reviewListingSuccess());
    };

    if (action === ReviewAction.ACCEPT) {
      const { statusCode } = await SharetribeApi.fetchSuppliersAndNotify(emailParams);
      if (statusCode === StatusCode.SUCCESS) handleSuccess('Email sent successfully');
    } else if (action === ReviewAction.REJECT) {
      const { statusCode } = await SharetribeApi.updateListingThroughIsdk({
        id: uuid,
        publicData: { isRejected: true },
      });
      if (statusCode === StatusCode.SUCCESS) handleSuccess('Listing rejected successfully');
    }
    dispatch(showListing(listing.id, config));
  } catch (error) {
    console.log(error, 'error');
    const storable = storableError(error);
    dispatch(reviewListingError(storable));
    showToaster('error', 'Operation failed');
    throw storable;
  }
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  return dispatch(showListing(listingId, config));
};
